import { EImportDestination } from "api/enums";
import { IImportationField, IImportationParameters } from "api/interfaces";
import { IImportationXLSXData } from "api/interfaces/IImportationXLSXData";
import { RequiredParamException } from "../exceptions/InvalidParamException";
import ContractImportRepository from "../repositories/ContractImportRepository";
import ContractRowService from "../services/ContractRowService";
import FileService from "../services/FileService";
import MeasurementRowService from "./MeasurementRowService";

class ContractImportService {
  /**
   * Esta funcao agenda a importação de um arquivo para o contrato passado.
   *  Após o agendamento o arquivo deve ser upado com o ID gerado.
   */
  public async importContractXLSX(
    contractUid: string,
    destination: EImportDestination,
    fields: IImportationField[],
    file: File,
    parameters: IImportationParameters,
    onUploadFinished: (newPath: string) => void,
    onUploadError: (error?: any) => void,
    onProgress: (data: IImportationXLSXData) => void
  ): Promise<void> {
    const fileName = file.name || "";
    this.validateData(contractUid, destination, fields, fileName, parameters);

    const newUid = await this.scheduleContractImport(
      contractUid,
      destination,
      fields,
      fileName,
      parameters
    );
    if (newUid) {
      this.addContractImportObserver(newUid, onProgress);

      FileService.uploadToImport(file, newUid)
        .then(onUploadFinished)
        .catch(onUploadError);

      ContractRowService.unsubscribeRows();
      if (destination === EImportDestination.MEASUREMENT) {
        MeasurementRowService.unsubscribeRows();
      }
    }
  }

  private validateData(
    contractUid: string,
    destination: EImportDestination,
    fields: IImportationField[],
    fileName: string,
    parameters: IImportationParameters
  ) {
    if (!contractUid) throw new RequiredParamException("uid");
    if (!destination) throw new RequiredParamException("destination");
    if (!fields?.length) throw new RequiredParamException("fields");
    if (!fileName) throw new RequiredParamException("fileName");

    if (parameters.tabIndex === undefined || parameters.tabIndex === null) {
      throw new RequiredParamException("tabIndex");
    }
    if (parameters.startRow === undefined || parameters.startRow === null) {
      throw new RequiredParamException("startRow");
    }
  }

  private async scheduleContractImport(
    uid: string,
    destination: EImportDestination,
    fields: IImportationField[],
    fileName: string,
    parameters: IImportationParameters
  ): Promise<string> {
    const repository = new ContractImportRepository();
    const data = {
      destination,
      id: uid,
      fields,
      fileTabIndex: parameters.tabIndex,
      startRowNumber: parameters.startRow,
      finalRowNumber: parameters.finalRow || null,
      measurementId: parameters.measurementId || null,
      attachmentsIds: parameters.attachmentsIds || null,
      user: {
        id: parameters.user?.uid || "",
        name: parameters.user?.name || "",
      },
    };

    return repository
      .scheduleContractImport(data, fileName)
      .then((response) => {
        const { status, uid, message } = response;
        if (status === "OK" && uid) {
          return uid;
        }

        throw new Error(`Error on scheculing the contract import: ${message}`);
      });
  }

  private addContractImportObserver(
    uid: string,
    onProgress: (data: IImportationXLSXData) => void
  ) {
    const repository = new ContractImportRepository();
    const observer = repository.subscribeImportSnapshot(uid, (data) => {
      onProgress(data);
      // Remove snapshot ao terminar
      if (data.progress === 100 || data.status === "error") {
        if (observer) observer();
      }
    });
  }
}

export default new ContractImportService();
